/**
 * Character Screen (HUNTER) — resumo rico do personagem: Combat Power, Level, Void Points,
 * arma equipada em destaque, cards de atributos com números grandes e breakdown por toque
 * (bottom sheet: Base/Level/Altar/Equipment/Void Tree). Identidade Void (azul/roxo/ciano).
 * Data-driven + responsivo (usa os tokens da Fase 5).
 */
import { PreviewRotor } from "./PreviewRotor";
import { i18n } from "../i18n/i18n";
import { fmtNum } from "../utils/format";

export interface StatSource {
  src: string;
  flat?: number;
  pct?: number;
}
export interface CharacterData {
  level: number;
  combatPower: number;
  voidAvailable: number;
  treeNodes: number;
  weapon: { name: string; rarity: string; icon: string; typeLabel: string } | null;
  masteryLabel: string;
  altarTotal: number;
  stats: {
    damage: number;
    maxHp: number;
    armor: number;
    moveSpeed: number;
    attackSpeed: number;
    critChance: number;
    critDamage: number;
    attackRange: number;
    hookRange: number;
    hookCooldown: number;
    sprintDuration: number;
    sprintCooldown: number;
    // 10.5K — atributos completos (tela ❗ da Bolsa)
    dps: number; // dano por segundo da mão (crit esperado)
    regen: number; // HP/s contínuo (Void Tree)
    lifeSteal: number; // 10.9EQ — fração do dano causado que vira vida
    /** 10.9EV — quanto veio de COMPRA (loja de moedas + VIP) e quantas compras */
    boughtHp: number; boughtHpN: number; boughtDmg: number; boughtDmgN: number; boughtRegen: number; boughtRegenN: number;
    dmgSlash: number; // +% cortante
    dmgPierce: number; // +% perfurante
    dmgBlunt: number; // +% contundente
    defSlash: number; // -% recebido cortante
    defPierce: number;
    defBlunt: number;
    block: number; // chance de bloqueio (0..1)
    resilience: number; // -% de todo dano recebido
    vsElite: number; // +% vs Elite/Boss
    execute: number; // +% vs alvo <30%
    chainCount: number; // armas presas na corrente
    chainDps: number; // DPS somado da corrente
    chainReach: number; // alcance da corrente (m)
  };
  breakdown: Record<string, StatSource[]>;
  /** 10.5O — missões ativas (nome, progresso e prêmio). */
  missions?: { name: string; progress: number; target: number; coins: number; xp: number }[];
  /** 10.9BY — resumo das skills ("Espada 12 · Defesa 8"); presente = mostra o botão Skills. */
  skillsLabel?: string;
}

type StatId = keyof CharacterData["stats"];

interface CardDef {
  id: StatId;
  labelKey: string;
  fmt: (v: number) => string;
  primary?: boolean;
}

const CARDS: CardDef[] = [
  { id: "damage", labelKey: "stat.damage", fmt: (v) => fmtNum(v), primary: true },
  { id: "maxHp", labelKey: "stat.maxHp", fmt: (v) => fmtK(v), primary: true },
  { id: "armor", labelKey: "stat.armor", fmt: (v) => String(Math.round(v)), primary: true },
  { id: "critChance", labelKey: "stat.criticalChance", fmt: (v) => `${Math.round(v * 100)}%` },
  { id: "critDamage", labelKey: "stat.criticalDamage", fmt: (v) => `${Math.round(v * 100)}%` },
  { id: "attackSpeed", labelKey: "stat.attackSpeed", fmt: (v) => `${v.toFixed(2)}/s` },
  { id: "moveSpeed", labelKey: "stat.moveSpeed", fmt: (v) => v.toFixed(1) },
  { id: "attackRange", labelKey: "stat.attackRange", fmt: (v) => `${v.toFixed(1)}m` },
  { id: "hookRange", labelKey: "stat.hookRange", fmt: (v) => `${v.toFixed(1)}m` },
  { id: "hookCooldown", labelKey: "stat.hookCooldown", fmt: (v) => `${v.toFixed(1)}s` },
  { id: "sprintDuration", labelKey: "stat.sprintDuration", fmt: (v) => `${v.toFixed(1)}s` },
  { id: "sprintCooldown", labelKey: "stat.sprintCooldown", fmt: (v) => `${v.toFixed(1)}s` },
  // 10.5K — os novos atributos também aparecem aqui (a lista completa fica na Bolsa ❗)
  { id: "dps", labelKey: "stat.dps", fmt: (v) => fmtNum(v) },
  { id: "regen", labelKey: "stat.regen", fmt: (v) => `${v.toFixed(1)}/s` },
  { id: "block", labelKey: "stat.block", fmt: (v) => `${Math.round(v * 100)}%` },
  { id: "resilience", labelKey: "stat.resilience", fmt: (v) => `${Math.round(v * 100)}%` },
];

function fmtK(v: number): string {
  return fmtNum(v); // 10.7P — K/M/B/T (era só K com 2 casas → "1847.29K" em milhões)
}

export class CharacterScreen {
  private readonly root: HTMLDivElement;
  private readonly grid: HTMLElement;
  private readonly sheet: HTMLElement;
  private data: CharacterData | null = null;

  /** 10.9BY — `onSkills` abre a tela de Skills/Treino a partir do painel do Herói. */
  constructor(uiRoot: HTMLElement, private readonly onClose: () => void, private readonly onSkills?: () => void) {
    this.root = document.createElement("div");
    this.root.className = "overlay char-overlay";
    this.root.hidden = true;
    this.root.innerHTML = `
      <div class="panel char-panel void-panel">
        <div class="ch-head">
          <h2 class="ch-title"></h2>
          <button class="btn ch-x" data-a="close">✕</button>
        </div>
        <div class="ch-summary">
          <div class="ch-cp"><span class="ch-cp-val"></span><span class="ch-cp-lbl"></span></div>
          <div class="ch-sub">
            <div class="ch-chip"><span class="ch-lvl-lbl"></span> <b class="ch-lvl"></b></div>
            <div class="ch-chip void"><span class="ch-vp-lbl"></span> <b class="ch-vp"></b></div>
          </div>
        </div>
        <div class="ch-hero">
          <canvas class="ch-3d pd-3d" width="192" height="240" aria-label="preview"></canvas>
          <div class="ch-weapon"></div>
        </div>
        <div class="ch-grid"></div>
        <div class="ch-progress"></div>
      </div>
      <div class="ch-sheet" hidden></div>`;
    uiRoot.appendChild(this.root);
    this.rotor.bind(this.root.querySelector<HTMLCanvasElement>(".ch-3d")!); // 10.9GZ
    this.grid = this.root.querySelector(".ch-grid")!;
    this.sheet = this.root.querySelector(".ch-sheet")!;
    this.root.querySelector('[data-a="close"]')!.addEventListener("click", () => this.onClose());
    this.root.addEventListener("click", (e) => {
      if (e.target === this.root) this.onClose();
    });
    i18n.onChange(() => { if (!this.root.hidden) this.render(); });
  }

  // 10.9GZ — prévia 3D (o Game renderiza o Caçador real aqui enquanto a tela está aberta)
  private readonly rotor = new PreviewRotor();
  get isOpen(): boolean { return !this.root.hidden; }
  get previewCanvas(): HTMLCanvasElement | null { return this.root.querySelector<HTMLCanvasElement>(".ch-3d"); }
  tickPreview(dt: number): number { return this.rotor.tick(dt); }

  open(data: CharacterData): void {
    this.data = data;
    this.sheet.hidden = true;
    this.root.hidden = false;
    this.render();
  }
  hide(): void {
    this.root.hidden = true;
  }

  private render(): void {
    const d = this.data;
    if (!d) return;
    this.root.querySelector(".ch-title")!.textContent = i18n.t("character.title");
    this.root.querySelector(".ch-cp-val")!.textContent = fmtK(d.combatPower);
    this.root.querySelector(".ch-cp-lbl")!.textContent = i18n.t("character.combatPower");
    this.root.querySelector(".ch-lvl-lbl")!.textContent = i18n.t("character.level");
    this.root.querySelector(".ch-lvl")!.textContent = String(d.level);
    this.root.querySelector(".ch-vp-lbl")!.textContent = i18n.t("voidTree.points");
    this.root.querySelector(".ch-vp")!.textContent = String(d.voidAvailable);

    const wp = this.root.querySelector<HTMLElement>(".ch-weapon")!;
    wp.innerHTML = d.weapon
      ? `<div class="chw-ic">${d.weapon.icon}</div><div class="chw-info"><div class="chw-name">${d.weapon.name}</div><div class="chw-sub">${d.weapon.rarity} · ${d.weapon.typeLabel}</div></div>`
      : `<div class="chw-sub">${i18n.t("equipment.slotLocked")}</div>`;

    this.grid.innerHTML = CARDS.map(
      (c) => `<button class="ch-card${c.primary ? " primary" : ""}" data-stat="${c.id}">
        <span class="cc-lbl">${i18n.t(c.labelKey)}</span>
        <span class="cc-val">${c.fmt(d.stats[c.id])}</span>
      </button>`,
    ).join("");
    this.grid.querySelectorAll<HTMLButtonElement>(".ch-card").forEach((el) => {
      el.onclick = () => this.openSheet(el.dataset.stat as StatId);
    });

    // 10.5O — MISSÕES ativas com prêmio (a lista completa fica aqui; o HUD mostra a atual)
    const ms = d.missions ?? [];
    const missionHtml = ms.length
      ? `<div class="chp-row"><span>${i18n.t("mission.title")}</span></div>` +
        ms
          .map((m) => {
            const pct = Math.max(0, Math.min(1, m.target > 0 ? m.progress / m.target : 0));
            return `<div class="ch-mission">
              <div class="cm-top"><span class="cm-name">${m.name}</span><span class="cm-rw">+${m.coins}🪙 +${m.xp}XP</span></div>
              <div class="cm-bar"><i style="width:${Math.round(pct * 100)}%"></i></div>
              <div class="cm-prog">${m.progress}/${m.target}</div>
            </div>`;
          })
          .join("")
      : "";

    void missionHtml; // 10.9FE — missoes tem tela propria (📜); aqui repetia e alongava a tela
    this.root.querySelector(".ch-progress")!.innerHTML =
      `<div class="chp-row"><span>${i18n.t("character.progression")}</span></div>` +
      `<div class="chp-line"><span>${i18n.t("voidTree.title")}</span><b>${d.treeNodes}</b></div>` +
      `<div class="chp-line"><span>${i18n.t("equipment.mastery")}</span><b>${d.masteryLabel}</b></div>` +
      `<div class="chp-line"><span>${i18n.t("altar.name")}</span><b>${d.altarTotal}</b></div>` +
      // 10.9BY — skills (resumo + botão pra tela completa)
      (d.skillsLabel !== undefined
        ? `<button class="btn ch-skills" data-a="skills"><span>${i18n.t("skills.title")}</span><b>${d.skillsLabel}</b></button>`
        : "");
    this.root.querySelector('[data-a="skills"]')?.addEventListener("click", () => this.onSkills?.());
  }

  private openSheet(stat: StatId): void {
    const d = this.data;
    if (!d) return;
    const card = CARDS.find((c) => c.id === stat)!;
    const sources = d.breakdown[stat] ?? [];
    const lines = sources
      .filter((s) => (s.flat ?? 0) !== 0 || (s.pct ?? 0) !== 0)
      .map((s) => {
        const val = s.pct !== undefined ? `+${Math.round(s.pct * 100)}%` : fmtSigned(s.flat ?? 0);
        return `<div class="cs-line"><span>${i18n.t(`source.${s.src}`)}</span><b>${val}</b></div>`;
      })
      .join("");
    this.sheet.innerHTML = `
      <div class="cs-inner void-panel">
        <div class="cs-head"><span class="cs-name">${i18n.t(card.labelKey)}</span><span class="cs-final">${card.fmt(d.stats[stat])}</span></div>
        <div class="cs-body">${lines || `<div class="cs-line"><span>${i18n.t("source.base")}</span><b>—</b></div>`}</div>
        <button class="btn primary cs-close" data-a="sclose">${i18n.t("menu.back")}</button>
      </div>`;
    this.sheet.hidden = false;
    this.sheet.querySelector('[data-a="sclose"]')!.addEventListener("click", () => (this.sheet.hidden = true));
    this.sheet.addEventListener("click", (e) => {
      if (e.target === this.sheet) this.sheet.hidden = true;
    });
  }
}

function fmtSigned(v: number): string {
  const r = Math.round(v * 100) / 100;
  return r >= 0 ? `+${r % 1 === 0 ? r : r.toFixed(2)}` : String(r);
}
